import { mainApi } from '../../../../api/mainApi';

import { middlewareApi } from '../../../middleware/middlewareApi';

import { AccessState } from '../../../../types';
import {
  User,
  UserProfile,
  UserProfileRolesResponse,
  UserProfilesResponse,
  UsersResponse,
} from '../interfaces/usersConfiguration.interfaces';
import { UserProfilePayload, UserProfileRolesPayload } from '../interfaces/payload.interface';

export const usersConfigurationTags = middlewareApi.enhanceEndpoints({
  addTagTypes: ['Users', 'UserProfiles', 'UserProfileRoles'],
});
// Define a service using a base URL and expected endpoints
export const usersConfigurationApi = usersConfigurationTags.injectEndpoints({
  // overrideExisting: false,

  endpoints: (builder) => ({
    /**
     * @GET all users
     */
    getUsers: builder.query<
      UsersResponse,
      { page: number; perPage: number; search: string; sort?: string; type_sort?: string }
    >({
      queryFn: async ({ page, search, perPage, type_sort, sort }, { getState }) => {
        const {
          access: { currentProfile },
        } = getState() as { access: AccessState };

        const user_profile_id = currentProfile?.profileId;

        try {
          const { data } = await mainApi.get<UsersResponse>('/users', {
            params: {
              user_profile_id,
              page,
              size: perPage,
              ...(sort && { sort }),
              type_sort: type_sort || 'desc',
              search,
            },
          });

          return { data };
        } catch (error: any) {
          return { error };
        }
      },
      providesTags: (result) =>
        result
          ? [
              ...result.data.map(({ id }) => ({ type: 'Users', id } as const)),
              { type: 'Users', id: 'LIST' },
            ]
          : [{ type: 'Users', id: 'LIST' }],
    }),

    /**
     * @GET user by id
     */
    getUser: builder.query<User, { userId: number }>({
      queryFn: async ({ userId }, { getState }) => {
        const {
          access: { currentProfile },
        } = getState() as { access: AccessState };

        const user_profile_id = currentProfile?.profileId;

        try {
          const { data } = await mainApi.get<User>(`/users/${userId}`, {
            params: {
              user_profile_id,
            },
          });

          return { data };
        } catch (error: any) {
          return { error };
        }
      },
      providesTags: (result, error, { userId }) => [{ type: 'Users', userId }],
    }),

    /**
     * @GET all user profiles
     */
    getUserProfiles: builder.query<UserProfilesResponse, { userId: number }>({
      queryFn: async ({ userId }, { getState }) => {
        const {
          access: { currentProfile },
        } = getState() as { access: AccessState };

        const user_profile_id = currentProfile?.profileId;

        try {
          const { data } = await mainApi.get<UserProfilesResponse>(`/users/${userId}/profiles`, {
            params: {
              user_profile_id,
            },
          });

          return { data };
        } catch (error: any) {
          return { error };
        }
      },
      providesTags: (result) => [{ type: 'UserProfiles', id: 'LIST' }],
    }),

    /**
     * @GET user profile by userId and profileId
     */
    getUserProfile: builder.query<UserProfile, { userId: number; profileId: number }>({
      queryFn: async ({ userId, profileId }, { getState }) => {
        const {
          access: { currentProfile },
        } = getState() as { access: AccessState };

        const user_profile_id = currentProfile?.profileId;

        try {
          const { data } = await mainApi.get<UserProfile>(
            `/users/${userId}/profiles/${profileId}`,
            {
              params: {
                user_profile_id,
              },
            }
          );

          return { data };
        } catch (error: any) {
          return { error };
        }
      },
      providesTags: (result, error, { userId }) => [{ type: 'UserProfiles', userId }],
    }),

    /**
     * @POST add profile to user
     */
    addUserProfile: builder.mutation<
      any,
      { userId: number; userProfilePayload: Partial<UserProfilePayload> }
    >({
      queryFn: async ({ userId, userProfilePayload }, { getState }) => {
        try {
          const {
            access: { currentProfile },
          } = getState() as { access: AccessState };

          const user_profile_id = currentProfile?.profileId;

          const { data } = await mainApi.post<any>(`/users/${userId}/profiles`, {
            user_profile_id,
            ...userProfilePayload,
          });
          return { data };
        } catch (error: any) {
          return { error };
        }
      },
      invalidatesTags: (result, error, { userId }) => [{ type: 'UserProfiles', userId }],
    }),

    /**
     * @PUT update UserProfile
     */
    updateUserProfile: builder.mutation<
      any,
      { userId: number; profileId: number; userProfilePayload: Partial<UserProfilePayload> }
    >({
      queryFn: async ({ userId, profileId, userProfilePayload }, { getState }) => {
        try {
          const {
            access: { currentProfile },
          } = getState() as { access: AccessState };

          const user_profile_id = currentProfile?.profileId;

          const { data } = await mainApi.put<any>(`/users/${userId}/profiles/${profileId}`, {
            user_profile_id,
            ...userProfilePayload,
          });

          return { data };
        } catch (error: any) {
          return { error };
        }
      },
      invalidatesTags: (result, error, { userId }) => [{ type: 'UserProfiles', userId }],
    }),

    /**
     * @DELETE remove userProfile
     */
    deleteUserProfile: builder.mutation<any, { userId: number; profileId: number }>({
      queryFn: async ({ userId, profileId }, { getState }) => {
        const {
          access: { currentProfile },
        } = getState() as { access: AccessState };

        const user_profile_id = currentProfile?.profileId;

        try {
          const { data } = await mainApi.delete(`/users/${userId}/profiles/${profileId}`, {
            data: {
              user_profile_id,
            },
          });

          return { data };
        } catch (error: any) {
          return { error };
        }
      },
      invalidatesTags: (result, error, { userId }) => [{ type: 'UserProfiles', userId }],
    }),

    /**
     * @DELETE remove userProfile
     */
    deleteAllUserProfile: builder.mutation<any, { userId: number }>({
      queryFn: async ({ userId }, { getState }) => {
        const {
          access: { currentProfile },
        } = getState() as { access: AccessState };

        const user_profile_id = currentProfile?.profileId;

        try {
          const { data } = await mainApi.delete(`/users/${userId}/profiles`, {
            data: {
              user_profile_id,
            },
          });

          return { data };
        } catch (error: any) {
          return { error };
        }
      },
      invalidatesTags: (result, error, { userId }) => [{ type: 'UserProfiles', userId }],
    }),

    /**
     * @GET all user profile roles
     */
    getUserProfileRoles: builder.query<
      UserProfileRolesResponse,
      { userId: number; profileId: number }
    >({
      queryFn: async ({ userId, profileId }, { getState }) => {
        const {
          access: { currentProfile },
        } = getState() as { access: AccessState };

        const user_profile_id = currentProfile?.profileId;

        try {
          const { data } = await mainApi.get<UserProfileRolesResponse>(
            `/users/${userId}/profiles/${profileId}/roles`,
            {
              params: {
                user_profile_id,
              },
            }
          );

          return { data };
        } catch (error: any) {
          return { error };
        }
      },
      providesTags: (result) => [{ type: 'UserProfileRoles', id: 'LIST' }],
    }),

    /**
     * @POST add role to profile to user
     */
    addUserProfileRoles: builder.mutation<
      any,
      { userId: number; profileId: number; userProfilePayload: Partial<UserProfileRolesPayload> }
    >({
      queryFn: async ({ userId, profileId, userProfilePayload }, { getState }) => {
        try {
          const {
            access: { currentProfile },
          } = getState() as { access: AccessState };

          const user_profile_id = currentProfile?.profileId;

          const { data } = await mainApi.post<any>(`users/${userId}/profiles/${profileId}/roles`, {
            user_profile_id,
            ...userProfilePayload,
          });

          return { data };
        } catch (error: any) {
          return { error };
        }
      },
      invalidatesTags: (result, error, { userId }) => [{ type: 'UserProfileRoles', id: 'LIST' }],
    }),

    /**
     * @DELETE remove userProfile
     */
    clearTokenClient: builder.mutation<any, { userId: number }>({
      queryFn: async ({ userId }, { getState }) => {
        const {
          access: { currentProfile },
        } = getState() as { access: AccessState };

        const user_profile_id = currentProfile?.profileId;

        try {
          const { data } = await mainApi.delete(`/users/${userId}/token_client`, {
            data: {
              user_profile_id,
            },
          });

          return { data };
        } catch (error: any) {
          return { error };
        }
      },
      invalidatesTags: (result, error, { userId }) => [{ type: 'Users', userId }],
    }),

    // /**
    //  * @GET all roles Avalibles
    //  */
    // getAvaliblesRoles: builder.query<Role[], { profileId: Number }>({
    //   queryFn: async ({ profileId }, { dispatch }) => {
    //     try {
    //       const { data } = await mainApi.get<Role[]>('/roles', {
    //         params: {
    //           user_profile_id: profileId,
    //           data: 'all',
    //         },
    //       });

    //       dispatch(setRolesList(data));

    //       return { data };
    //     } catch (error: any) {
    //       return { error };
    //     }
    //   },
    //   providesTags: [{ type: 'RolesAvailables', id: 'COMBO_ROLES_LIST' }],
    // }),

    //
  }),
});

// Export hooks for usage in functional components, which are
// auto-generated based on the defined endpoints
export const {
  useGetUsersQuery,
  useGetUserQuery,
  useLazyGetUserQuery,

  useGetUserProfilesQuery,
  useGetUserProfileQuery,
  useLazyGetUserProfileQuery,
  useAddUserProfileMutation,
  useUpdateUserProfileMutation,
  useDeleteUserProfileMutation,

  useGetUserProfileRolesQuery,
  useAddUserProfileRolesMutation,

  useClearTokenClientMutation,
} = usersConfigurationApi;
